/**
 * USD-only formatter. Correct for surfaces that are inherently dollar-denominated (cloud spend,
 * SHI-internal reporting). For anything priced by a storefront -- product cards, cart, checkout,
 * quotes -- use `createCurrencyFormatter` with the org unit's currency instead, because a non-USD
 * storefront's prices arrive already converted and only need labelling.
 */
export const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

/**
 * USD without cents, e.g. "$1,250". For figures where the cents carry no meaning and would only
 * add noise -- spend summaries, KPI tiles, chart values.
 */
export const usdWholeDollarFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/**
 * USD abbreviated to one decimal, e.g. "$1.5K", "$2.4M". For labels in constrained layouts such
 * as chart bars, where the full figure will not fit. Keeps the sign, so a credit reads "-$5K".
 */
export const usdCompactFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/**
 * Builds an `Intl.NumberFormat` for a currency. Falls back to USD so a missing or malformed
 * currency still renders an amount rather than throwing.
 *
 * `Intl.NumberFormat` applies each currency's own decimal rules, so zero-decimal currencies (JPY,
 * KRW) render correctly without special-casing. When supplied, `currencySymbol` replaces only the
 * currency part, preserving the locale's number style and symbol placement. `wholeUnits` drops the
 * minor units, e.g. "$1,250", for summary figures where cents add only noise.
 */
export function createCurrencyFormatter(
  locale?: string | null,
  currencyCode?: string | null,
  currencySymbol?: string | null,
  { wholeUnits = false }: { wholeUnits?: boolean } = {},
): Intl.NumberFormat {
  const fallback = wholeUnits ? usdWholeDollarFormatter : usdFormatter;
  const normalizedCurrencyCode = currencyCode?.trim().toUpperCase() || "USD";
  if (!STOREFRONT_CURRENCY_CODES.has(normalizedCurrencyCode)) {
    return fallback;
  }

  try {
    const formatter = new Intl.NumberFormat(locale || "en-US", {
      style: "currency",
      currency: normalizedCurrencyCode,
      ...(wholeUnits && { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
    });
    if (!currencySymbol) {
      return formatter;
    }

    const formatParts = formatter.formatToParts.bind(formatter);
    const replaceCurrencyPart = (value: number | bigint) =>
      formatParts(value).map((part) =>
        part.type === "currency" ? { ...part, value: currencySymbol } : part,
      );
    Object.defineProperty(formatter, "formatToParts", {
      value: replaceCurrencyPart,
    });
    Object.defineProperty(formatter, "format", {
      value: (value: number | bigint) =>
        replaceCurrencyPart(value)
          .map((part) => part.value)
          .join(""),
    });
    return formatter;
  } catch {
    // Intl throws RangeError on an unknown currency code or malformed locale. Reference data can
    // carry non-ISO codes, and a price label is never worth crashing a page over.
    return fallback;
  }
}

/**
 * Formats a single amount in the given currency. Prefer a memoized `createCurrencyFormatter` when
 * formatting many values (grids, cart line lists).
 */
export function formatCurrency(
  value: number,
  locale?: string | null,
  currencyCode?: string | null,
  currencySymbol?: string | null,
): string {
  return createCurrencyFormatter(locale, currencyCode, currencySymbol).format(
    value,
  );
}

interface CurrencyDisplay {
  code: string;
  symbol: string;
  locale: string;
}

interface CurrencyReference extends Omit<CurrencyDisplay, "code"> {
  ids: readonly number[];
}

/**
 * Display data for currencies saved on records (orders, quotes, approvals). Mirrors the backend
 * QuoteCurrencyReference so a record renders the same way as the storefront that created it.
 */
const CURRENCY_REFERENCE: Record<string, CurrencyReference> = {
  USD: { symbol: "$", locale: "en-US", ids: [1] },
  GBP: { symbol: "£", locale: "en-GB", ids: [3] },
  EUR: { symbol: "€", locale: "fr-FR", ids: [5, 26] },
  HKD: { symbol: "HK$", locale: "zh-HK", ids: [6, 27] },
  CAD: { symbol: "CA$", locale: "en-CA", ids: [8] },
  DKK: { symbol: "DKK", locale: "da-DK", ids: [9] },
  JPY: { symbol: "¥", locale: "ja-JP", ids: [11] },
  NOK: { symbol: "NOK", locale: "nb-NO", ids: [16] },
  SEK: { symbol: "SEK", locale: "sv-SE", ids: [18, 29] },
  AUD: { symbol: "A$", locale: "en-AU", ids: [19, 28] },
  CNY: { symbol: "CN¥", locale: "zh-CN", ids: [20] },
  CHF: { symbol: "CHF", locale: "de-CH", ids: [22, 30] },
  INR: { symbol: "₹", locale: "en-IN", ids: [23] },
  BRL: { symbol: "R$", locale: "pt-BR", ids: [24] },
  SGD: { symbol: "S$", locale: "en-SG", ids: [25] },
  KRW: { symbol: "₩", locale: "ko-KR", ids: [31] },
};

const STOREFRONT_CURRENCY_CODES = new Set(Object.keys(CURRENCY_REFERENCE));

// Legacy SHISystem abbreviations that differ from the ISO code.
const CURRENCY_CODE_ALIASES: Record<string, string> = {
  RMB: "CNY",
  "EUR(IRL)": "EUR",
  "HKD(HK)": "HKD",
  "AUD(HK)": "AUD",
  "SEK(IRL)": "SEK",
  "CHF(IRL)": "CHF",
};

const CURRENCY_CODE_BY_ID: Record<number, string> = Object.entries(
  CURRENCY_REFERENCE,
).reduce<Record<number, string>>((currencyCodesById, [code, currency]) => {
  for (const id of currency.ids) {
    currencyCodesById[id] = code;
  }
  return currencyCodesById;
}, {});

function resolveCurrencyCode(currency?: string | number | null): string | undefined {
  const code =
    typeof currency === "number"
      ? CURRENCY_CODE_BY_ID[currency]
      : currency
        ? (CURRENCY_CODE_ALIASES[currency.trim().toUpperCase()] ??
          currency.trim().toUpperCase())
        : undefined;
  return code && CURRENCY_REFERENCE[code] ? code : undefined;
}

/** Whether a saved ISO code or SHISystem currency ID has display data, rather than falling back to USD. */
export function isKnownCurrency(currency?: string | number | null): boolean {
  return resolveCurrencyCode(currency) !== undefined;
}

/** Resolves display data for a saved ISO code or SHISystem currency ID; unknown values fall back to USD. */
export function getCurrencyDisplay(
  currency?: string | number | null,
): CurrencyDisplay {
  const resolvedCode = resolveCurrencyCode(currency) ?? "USD";
  const display = CURRENCY_REFERENCE[resolvedCode];
  return { code: resolvedCode, symbol: display.symbol, locale: display.locale };
}

const savedCurrencyFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Formatter for an amount saved in a specific currency (an order's or quote's own currency).
 * Formatters are cached per resolved currency, so grid cells can call this per row.
 */
export function createSavedCurrencyFormatter(
  currency?: string | number | null,
  { wholeUnits = false }: { wholeUnits?: boolean } = {},
): Intl.NumberFormat {
  const display = getCurrencyDisplay(currency);
  const cacheKey = wholeUnits ? `${display.code}:whole` : display.code;
  let formatter = savedCurrencyFormatters.get(cacheKey);
  if (!formatter) {
    formatter = createCurrencyFormatter(display.locale, display.code, display.symbol, {
      wholeUnits,
    });
    savedCurrencyFormatters.set(cacheKey, formatter);
  }
  return formatter;
}
